import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { URL_BASE } from '../const/general';
import { fetchAuthSession } from 'aws-amplify/auth';

// =========================
// 共通SWRロジック
// =========================

/**
 * SWRのリトライ処理（最大2回、404/500では停止）
 */
const handleRetry = (error, _key, _config, revalidate, { retryCount }) => {
  if (error._isRefreshFailed || [404, 500].includes(error.status)) return;
  if (retryCount >= 2) return;
  setTimeout(() => revalidate({ retryCount: retryCount + 1 }), 5000);
};

/**
 * SWR共通フック（fetcherと認証失敗時の遷移フラグ指定）
 */
const useCommonSWR = (url, fetcher, navigateOnFail = true) => {
  const navigate = useNavigate();
  const fullUrl = URL_BASE + url;

  const { data, error } = useSWR(
    [fullUrl],
    async ([url]) => {
      try {
        return await fetcher(url);
      } catch (err) {
        if ((err._is401 || err._isRefreshFailed) && navigateOnFail) {
          navigate('/signin');
        }
        throw err;
      }
    },
    {
      onErrorRetry: handleRetry,
      refreshInterval: 300000,
    },
    {
      dedupingInterval: 500, // 500ms以内は再リクエストしない
    }
  );

  return {
    data,
    loading: !error && !data,
    error,
  };
};

// =========================
// カスタムフック
// =========================

/**
 * データをポーリングする関数
 */
export const useDataPolling = (url) => useCommonSWR(url, fetchWithToken);

/**
 * データをS3から取得する関数
 */
export const useDataFromS3 = (url) => useCommonSWR(url, fetchFromPresignedUrl);

// =========================
// データ取得系
// =========================

/**
 * URLを加工しAPI取得
 */
export const fetchWithUrl = async (url) => {
  const rest_url = URL_BASE + url
  const res_data = await fetchWithToken(rest_url)
  return res_data
}

/**
 * 認証トークン付きでAPI取得
 */
export const fetchWithToken = async (url, alreadyRetried = false) => {
  let session;
  try {
    session = await fetchAuthSession();
  } catch {
    const error = new Error("セッション取得に失敗");
    error._is401 = true;
    throw error;
  }

  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    const error = new Error("idTokenが取得できません");
    error._is401 = true;
    throw error;
  }

  try {
    const res = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });
    const contentType = res.headers.get("content-type");

    // HTMLが返ってきているときはエラーとして無視
    if (contentType && contentType.includes("text/html")) {
      const text = await res.text();
      throw new Error("Received HTML instead of JSON: " + text.slice(0, 50));
    }
    return res.data;
  } catch (error) {
    if (error.response?.status === 401 && !alreadyRetried) {
      return await refreshTokenAndRetry(url);
    }
    throw error;
  }
};

/**
 * トークンリフレッシュ後に再試行
 */
const refreshTokenAndRetry = async (url) => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) throw new Error("リフレッシュ後のidTokenなし");

    console.log("トークンリフレッシュ成功");
    return await fetchWithToken(url, true);
  } catch (err) {
    err._isRefreshFailed = true;
    throw err;
  }
};

/**
 * Presigned URLでS3から取得
 */
export const fetchFromPresignedUrl = async (apiUrl) => {
  try {
    const presignedUrl = await getPresignedUrlWithRefresh(apiUrl);
    // const res = await axios.get(presignedUrl);
    const res = await axios.get(presignedUrl, {
      // headers は完全に空にするのが安全
      headers: {},
      withCredentials: false
    });
    return res.data;
  } catch (error) {
    if (error.response?.status === 401) error._is401 = true;
    throw error;
  }
};

/**
 * Presigned URLの取得処理（トークン付き）
 */
const getPresignedUrlWithRefresh = async (url, alreadyRetried = false) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) throw new Error("idTokenなし");

    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    return res.data.downloadUrls;
  } catch (error) {
    if (error.response?.status === 401 && !alreadyRetried) {
      return await getPresignedUrlWithRefresh(url, true);
    }
    throw error;
  }
};
